import type { Id, ScriptDocument } from "@supermanus/document-model";
import { normalizeDocument } from "@supermanus/document-model";

export interface ProjectMeta {
  id: Id;
  title: string;
  updatedAt: string;
  /** When the production was first created (ISO). Falls back to updatedAt if unknown. */
  createdAt?: string;
}

export interface LocalStore {
  listProjects(): Promise<ProjectMeta[]>;
  loadProject(id: Id): Promise<ScriptDocument | null>;
  saveProject(doc: ScriptDocument): Promise<void>;
  deleteProject(id: Id): Promise<void>;
}

const DB_NAME = "supermanus";
const STORE = "productions";
const DB_VERSION = 2;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "production.id" });
      }
      // Drop legacy store if present
      if (db.objectStoreNames.contains("projects")) {
        db.deleteObjectStore("projects");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** IndexedDB-backed store for browser / Tauri webview without native SQL. */
export class IndexedDbStore implements LocalStore {
  async listProjects(): Promise<ProjectMeta[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        const docs = ((req.result as unknown[]) ?? []).map((d) =>
          normalizeDocument(d),
        );
        resolve(
          docs
            .map((d) => ({
              id: d.production.id,
              title: d.production.title,
              updatedAt: d.production.updatedAt,
              createdAt: d.production.createdAt || d.production.updatedAt,
            }))
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        );
      };
      req.onerror = () => reject(req.error);
    });
  }

  async loadProject(id: Id): Promise<ScriptDocument | null> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => {
        if (!req.result) {
          resolve(null);
          return;
        }
        resolve(normalizeDocument(req.result));
      };
      req.onerror = () => reject(req.error);
    });
  }

  async saveProject(doc: ScriptDocument): Promise<void> {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(doc);
    await txDone(tx);
  }

  async deleteProject(id: Id): Promise<void> {
    const db = await openDb();
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(id);
    await txDone(tx);
  }
}

/**
 * SQLite schema used by Tauri `@tauri-apps/plugin-sql`.
 * Documents are stored as JSON blobs for v1 simplicity and schema evolution.
 */
export const SQLITE_SCHEMA = `
CREATE TABLE IF NOT EXISTS projects (
  id TEXT PRIMARY KEY NOT NULL,
  title TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  document_json TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_projects_updated ON projects(updated_at);
`;

export interface SqlDatabase {
  execute(query: string, bindValues?: unknown[]): Promise<unknown>;
  select<T>(query: string, bindValues?: unknown[]): Promise<T[]>;
}

/** SQLite-backed store (Tauri plugin-sql or any compatible driver). */
export class SqliteStore implements LocalStore {
  constructor(private db: SqlDatabase) {}

  static async open(db: SqlDatabase): Promise<SqliteStore> {
    await db.execute(SQLITE_SCHEMA);
    return new SqliteStore(db);
  }

  async listProjects(): Promise<ProjectMeta[]> {
    const rows = await this.db.select<{
      id: string;
      title: string;
      updated_at: string;
      document_json: string;
    }>(
      "SELECT id, title, updated_at, document_json FROM projects ORDER BY updated_at DESC",
    );
    return rows.map((r) => {
      let createdAt = r.updated_at;
      try {
        const doc = JSON.parse(r.document_json) as {
          production?: { createdAt?: string };
        };
        if (doc.production?.createdAt) createdAt = doc.production.createdAt;
      } catch {
        /* keep updated_at */
      }
      return {
        id: r.id,
        title: r.title,
        updatedAt: r.updated_at,
        createdAt,
      };
    });
  }

  async loadProject(id: Id): Promise<ScriptDocument | null> {
    const rows = await this.db.select<{ document_json: string }>(
      "SELECT document_json FROM projects WHERE id = $1",
      [id],
    );
    if (!rows[0]) return null;
    return normalizeDocument(JSON.parse(rows[0].document_json));
  }

  async saveProject(doc: ScriptDocument): Promise<void> {
    await this.db.execute(
      `INSERT INTO projects (id, title, updated_at, document_json)
       VALUES ($1, $2, $3, $4)
       ON CONFLICT(id) DO UPDATE SET
         title = excluded.title,
         updated_at = excluded.updated_at,
         document_json = excluded.document_json`,
      [
        doc.production.id,
        doc.production.title,
        doc.production.updatedAt,
        JSON.stringify(doc),
      ],
    );
  }

  async deleteProject(id: Id): Promise<void> {
    await this.db.execute("DELETE FROM projects WHERE id = $1", [id]);
  }
}

export async function createLocalStore(): Promise<LocalStore> {
  try {
    const tauri = (globalThis as { __TAURI__?: unknown }).__TAURI__;
    if (tauri) {
      const mod = (await import(
        /* @vite-ignore */ "@tauri-apps/plugin-sql"
      )) as {
        default: {
          load: (path: string) => Promise<SqlDatabase>;
        };
      };
      const db = await mod.default.load("sqlite:supermanus.db");
      return SqliteStore.open(db);
    }
  } catch {
    // Fall through to IndexedDB
  }
  return new IndexedDbStore();
}

export type RevisionTracker = {
  getRevision(projectId: Id): number;
  setRevision(projectId: Id, revision: number): void;
};

export class RevisionConflictError extends Error {
  readonly code = "revision_conflict";

  constructor(
    public document: ScriptDocument,
    public revision: number,
  ) {
    super("Revision conflict");
    this.name = "RevisionConflictError";
  }
}

export class NetworkError extends Error {
  readonly code = "network";

  constructor(message = "Ingen forbindelse") {
    super(message);
    this.name = "NetworkError";
  }
}

export function isNetworkError(err: unknown): boolean {
  if (err instanceof NetworkError) return true;
  if (err instanceof TypeError) return true;
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /failed to fetch|networkerror|load failed|ingen forbindelse/i.test(
    msg,
  );
}

type ApiError = Error & { status?: number; body?: unknown };

const REQUEST_TIMEOUT_MS = 8_000;

async function apiJson<T>(
  apiBase: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  let res: Response;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    res = await fetch(`${apiBase.replace(/\/$/, "")}${path}`, {
      credentials: "include",
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch (err) {
    throw new NetworkError(
      err instanceof Error ? err.message : "Ingen forbindelse",
    );
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = { error: text };
    }
  }
  if (!res.ok) {
    const payload = data as {
      code?: string;
      document?: ScriptDocument;
      revision?: number;
      error?: string;
      message?: string;
    } | null;
    if (res.status === 409 && payload?.code === "revision_conflict" && payload.document) {
      throw new RevisionConflictError(
        normalizeDocument(payload.document),
        Number(payload.revision) || 0,
      );
    }
    const err: ApiError = new Error(
      payload?.error || payload?.message || `HTTP ${res.status}`,
    );
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data as T;
}

/** MySQL-backed store used by the hosted PHP web app. */
export class HttpStore implements LocalStore, RevisionTracker {
  private revisions = new Map<string, number>();

  constructor(private apiBase = "/api") {}

  getRevision(projectId: Id): number {
    return this.revisions.get(projectId) ?? 0;
  }

  setRevision(projectId: Id, revision: number): void {
    this.revisions.set(projectId, revision);
  }

  async listProjects(): Promise<ProjectMeta[]> {
    const data = await apiJson<{ projects: ProjectMeta[] }>(
      this.apiBase,
      "/projects",
    );
    return data.projects ?? [];
  }

  async loadProject(id: Id): Promise<ScriptDocument | null> {
    try {
      const data = await apiJson<{
        revision: number;
        document: ScriptDocument;
      }>(this.apiBase, `/projects/${encodeURIComponent(id)}`);
      this.setRevision(id, data.revision);
      return normalizeDocument(data.document);
    } catch (err) {
      if ((err as ApiError).status === 404) return null;
      throw err;
    }
  }

  async saveProject(doc: ScriptDocument): Promise<void> {
    const id = doc.production.id;
    if (!this.revisions.has(id)) {
      const data = await apiJson<{ revision: number }>(this.apiBase, "/projects", {
        method: "POST",
        body: JSON.stringify({ document: doc }),
      });
      this.setRevision(id, data.revision);
      return;
    }
    const data = await apiJson<{ revision: number }>(
      this.apiBase,
      `/projects/${encodeURIComponent(id)}`,
      {
        method: "PUT",
        body: JSON.stringify({
          document: doc,
          baseRevision: this.getRevision(id),
        }),
      },
    );
    this.setRevision(id, data.revision);
  }

  async deleteProject(id: Id): Promise<void> {
    await apiJson(this.apiBase, `/projects/${encodeURIComponent(id)}`, {
      method: "DELETE",
    });
    this.revisions.delete(id);
  }
}

export function createHttpStore(apiBase = "/api"): HttpStore {
  return new HttpStore(apiBase);
}

export {
  loadProjectSortMode,
  saveProjectSortMode,
  loadProjectCustomOrder,
  saveProjectCustomOrder,
  sortProjectList,
  mergeProjectCustomOrder,
} from "./projectListSort";
export type { ProjectSortMode } from "./projectListSort";

